import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useBookkeeperEnabled } from "./useBookkeeperEnabled";
import { Button } from "@/components/ui/button";
import { useBookkeeperChat, BK_MIN_WIDTH, BK_MAX_WIDTH } from "./BookkeeperChatProvider";
import { BookkeeperChatSurface, BOOKKEEPER_HEADER_BUTTON_CLASS } from "./BookkeeperChatSurface";

/**
 * Docked, resizable right-side assistant panel (Cursor/VS Code style). Gated by
 * the `bookkeeper_ai` feature flag (`useBookkeeperEnabled`) — every org role; the API filters Ré's tools
 * per role. The data-hook-bearing inner component only
 * mounts while the panel is open, so its queries don't fire when it's closed.
 *
 * The panel owns only its chrome — the sized `<aside>`, the drag-to-resize
 * handle and the close button. The chat itself lives in `BookkeeperChatSurface`,
 * which renders a fragment so this `<aside>` stays the chat's only container
 * (the phone surface at `/apps/bookkeeper` supplies its own).
 */
export function BookkeeperPanel() {
  const enabled = useBookkeeperEnabled();
  const chat = useBookkeeperChat();
  if (!enabled || !chat.isAvailable || !chat.isOpen) return null;
  return <BookkeeperPanelInner />;
}

function BookkeeperPanelInner() {
  const { t } = useTranslation("common");
  const chat = useBookkeeperChat();

  // ── Drag-to-resize (left edge) ─────────────────────────────────────────────
  // Handlers must stay STABLE across the drag: reading width/setWidth from refs
  // keeps `onDragStart` dependency-free, so re-renders during the drag (each
  // width change) don't recreate listeners or fire a stale cleanup mid-drag.
  const setWidthRef = useRef(chat.setWidth);
  setWidthRef.current = chat.setWidth;
  const widthRef = useRef(chat.widthPx);
  widthRef.current = chat.widthPx;
  const cleanupDragRef = useRef<(() => void) | null>(null);

  const onDragStart = useCallback((e: React.PointerEvent) => {
    const startX = e.clientX;
    const startWidth = widthRef.current;
    document.body.style.userSelect = "none";
    // Dragging left (smaller clientX) widens the panel.
    const move = (ev: PointerEvent) => setWidthRef.current(startWidth + (startX - ev.clientX));
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      document.body.style.userSelect = "";
      cleanupDragRef.current = null;
    };
    cleanupDragRef.current = end;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
  }, []);

  // Clean up a drag still in progress if the panel unmounts.
  useEffect(() => () => cleanupDragRef.current?.(), []);

  return (
    <aside
      className="relative flex h-full shrink-0 flex-col border-l border-border bg-background text-foreground"
      style={{ width: chat.widthPx, minWidth: BK_MIN_WIDTH, maxWidth: BK_MAX_WIDTH }}
      aria-label={t("bookkeeper.title")}
    >
      {/* Drag handle (left edge) */}
      <div
        role="separator"
        aria-orientation="vertical"
        onPointerDown={onDragStart}
        className="absolute left-0 top-0 z-10 h-full w-1 cursor-col-resize hover:bg-primary/30"
      />

      <BookkeeperChatSurface
        target="panel"
        headerActions={
          <Button
            variant="ghost"
            size="icon"
            className={BOOKKEEPER_HEADER_BUTTON_CLASS}
            title={t("bookkeeper.close")}
            onClick={() => chat.close()}
          >
            <X className="h-4 w-4" />
          </Button>
        }
      />
    </aside>
  );
}
